import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import { adminTenantsService } from '@/services/control/admin-tenants-service';
import type { AdminTenant } from '@/types/control/tenant';

import { adminQueryKeys } from './query-keys';
import { retryControlApi } from './retry';

/**
 * The tenant list every admin page picks from. One query key, so five pages
 * share one cached read per stale window rather than each fetching its own.
 *
 * The refetch overrides are what make that true: `main.tsx` sets
 * `refetchOnMount: 'always'` and `refetchOnWindowFocus: 'always'` globally, and
 * both beat a per-query `staleTime` — so without them, navigating between admin
 * pages refetches every time and the shared key buys only deduplication of
 * concurrent calls. The tenant list changes rarely, and creating a tenant
 * invalidates this key directly.
 */
export function useAdminTenants(): UseQueryResult<AdminTenant[], Error> {
  return useQuery<AdminTenant[], Error>({
    queryKey: adminQueryKeys.tenants(),
    queryFn: ({ signal }) => adminTenantsService.list(signal),
    staleTime: 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: retryControlApi,
  });
}
